import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ref, onValue } from 'firebase/database';
import { database } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';
import './Poetry.css';

// Import viewing option components
import BreathView from '../components/poetry-views/BreathView';
import ConstellationsView from '../components/poetry-views/ConstellationsView';
import WindBlownView from '../components/poetry-views/WindBlownView';
import RainView from '../components/poetry-views/RainView';
import CampfireView from '../components/poetry-views/CampfireView';
import OttoView from '../components/poetry-views/OttoView';

const Poetry = () => {
  const { currentUser } = useAuth();
  const { markAsRead, isPoemRead } = useNotification();
  const navigate = useNavigate();
  const location = useLocation();

  const [viewMode, setViewMode] = useState('viewingTypes'); // 'viewingTypes', 'poems', or 'reading'
  const [selectedViewingType, setSelectedViewingType] = useState(null);
  const [selectedPoem, setSelectedPoem] = useState(null);
  const [poems, setPoems] = useState([]);

  // Viewing type configurations
  const viewingTypes = [
    {
      id: 'breath',
      name: 'Breath',
      readLabel: 'Breathe and Listen',
      color: '#FFD4A3',
      component: BreathView
    },
    {
      id: 'constellations',
      name: 'Constellations',
      readLabel: 'Stargaze',
      color: '#4A5568',
      component: ConstellationsView
    },
    {
      id: 'windBlown',
      name: 'Wind-Blown',
      readLabel: 'Watch the Leaves Fall',
      color: '#D97706',
      component: WindBlownView
    },
    {
      id: 'rain',
      name: 'Rain',
      readLabel: 'Listen to and Feel the Rain',
      color: '#60A5FA',
      component: RainView
    },
    {
      id: 'campfire',
      name: 'Campfire',
      readLabel: 'Hear a Campfire Reading',
      color: '#F97316',
      component: CampfireView
    }
  ];

  // Fetch poetry from Firebase
  useEffect(() => {
    const poetryRef = ref(database, 'poetry/entries');
    const unsubscribe = onValue(poetryRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const poemsArray = Object.entries(data).map(([id, poem]) => ({
          id,
          ...poem
        }));
        setPoems(poemsArray);
      }
    });

    return () => unsubscribe();
  }, []);

  // Handle direct navigation from bird notification
  useEffect(() => {
    if (location.state?.poemId) {
      console.log('Direct navigation triggered. PoemId:', location.state.poemId);
      console.log('Poems loaded:', poems.length);
      console.log('All poems:', poems);

      if (poems.length > 0) {
        const poem = poems.find(p => p.id === location.state.poemId);
        console.log('Found poem:', poem);

        if (poem) {
          const viewingType = viewingTypes.find(vt => vt.id === poem.medium);
          console.log('Viewing type:', viewingType);

          setSelectedPoem(poem);
          setSelectedViewingType(viewingType);
          setViewMode('reading');
          // Mark poem as read when opened via bird notification
          markAsRead(poem.id);
        } else {
          console.error('Poem not found with ID:', location.state.poemId);
        }
      } else {
        console.log('Waiting for poems to load...');
      }
    }
  }, [location.state, poems, markAsRead, viewingTypes]);

  const handleViewingTypeClick = (viewingType) => {
    setSelectedViewingType(viewingType);
    setViewMode('poems');
  };

  const handlePoemClick = (poem) => {
    setSelectedPoem(poem);
    setViewMode('reading');
    // Mark poem as read when clicked
    if (poem.authorEmail !== currentUser?.email) {
      markAsRead(poem.id);
    }
  };

  const handleBackToViewingTypes = () => {
    setViewMode('viewingTypes');
    setSelectedViewingType(null);
  };

  const handleBackToPoems = () => {
    setViewMode('poems');
    setSelectedPoem(null);
  };

  // Filter poems by selected viewing type
  const filteredPoems = selectedViewingType
    ? poems.filter(p => p.medium === selectedViewingType.id)
    : [];

  // Check if a viewing type has any unread poems
  const hasUnreadPoemsForType = (viewingTypeId) => {
    return poems.some(poem =>
      poem.medium === viewingTypeId &&
      poem.authorEmail !== currentUser?.email &&
      !isPoemRead(poem.id)
    );
  };

  // Render the selected poem with its viewing component
  const renderPoemView = () => {
    if (!selectedPoem || !selectedViewingType) return null;

    const ViewComponent = selectedViewingType.component;
    return (
      <ViewComponent
        poem={selectedPoem}
        onBack={handleBackToPoems}
      />
    );
  };

  return (
    <div className="poetry-page">
      <AnimatePresence mode="wait">
        {viewMode === 'viewingTypes' && (
          <motion.div
            key="viewingTypes"
            className="viewing-types-container"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="poetry-title">Poetry Garden</h1>
            <p className="poetry-subtitle">Choose a way to experience the words...</p>

            <div className="vine-garden-vertical">
              {/* Detailed Vertical Vine with Thorns - Background */}
              <svg className="vertical-vine-svg" viewBox="0 0 400 1000" preserveAspectRatio="xMidYMid meet">
                <defs>
                  <linearGradient id="vineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#2D5016" />
                    <stop offset="50%" stopColor="#4A7C59" />
                    <stop offset="100%" stopColor="#2D5016" />
                  </linearGradient>
                </defs>

                {/* Main vertical vine - thick and detailed */}
                <motion.path
                  d="M 200 50 Q 180 150, 200 250 T 200 450 Q 220 550, 200 650 T 200 900"
                  stroke="url(#vineGradient)"
                  strokeWidth="18"
                  fill="none"
                  strokeLinecap="round"
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{ duration: 3, ease: "easeInOut" }}
                />

                {/* Vine texture lines */}
                <motion.path
                  d="M 200 50 Q 185 150, 200 250 T 200 450 Q 215 550, 200 650 T 200 900"
                  stroke="#6B8E23"
                  strokeWidth="3"
                  fill="none"
                  opacity="0.4"
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{ duration: 3, delay: 0.2 }}
                />

                {/* Thorns along the vine */}
                {[...Array(24)].map((_, i) => {
                  const y = 80 + i * 35;
                  const x = 200 + (i % 2 === 0 ? 9 : -9);
                  const direction = i % 2 === 0 ? 1 : -1;
                  return (
                    <motion.path
                      key={`thorn-${i}`}
                      d={`M ${x} ${y} L ${x + direction * 12} ${y - 8} L ${x + direction * 8} ${y + 2} Z`}
                      fill="#8B4513"
                      stroke="#654321"
                      strokeWidth="0.5"
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{ scale: 1, opacity: 0.9 }}
                      transition={{ delay: 1 + i * 0.05, duration: 0.3 }}
                    />
                  );
                })}

                {/* Small leaves scattered along vine */}
                {[...Array(16)].map((_, i) => {
                  const y = 100 + i * 50;
                  const x = 200 + ((i % 3) - 1) * 25;
                  return (
                    <motion.ellipse
                      key={`leaf-${i}`}
                      cx={x}
                      cy={y}
                      rx="12"
                      ry="18"
                      fill="#6B8E23"
                      opacity="0.6"
                      transform={`rotate(${(i % 2) * 60 - 30} ${x} ${y})`}
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{ scale: 1, opacity: 0.6 }}
                      transition={{ delay: 0.8 + i * 0.08, duration: 0.4 }}
                    />
                  );
                })}
              </svg>

              {/* Emoji-like Flowers on the vine */}
              <div className="flowers-on-vertical-vine">
                {/* Back flower - returns to home */}
                <motion.div
                  className="vertical-vine-flower flower-back"
                  style={{
                    cursor: 'pointer',
                    top: '5%'
                  }}
                  initial={{ scale: 0, rotate: -180, opacity: 0 }}
                  animate={{ scale: 1, rotate: 0, opacity: 1 }}
                  transition={{ delay: 1.2, type: "spring", stiffness: 180, damping: 12 }}
                  whileHover={{ scale: 1.2, rotate: [0, -5, 5, -5, 0], transition: { duration: 0.5 } }}
                  onClick={() => navigate('/home')}
                >
                  <div className="flower-emoji" style={{ fontSize: '80px' }}>
                    🌸
                  </div>
                  <div className="vertical-vine-flower-label">
                    Return to Home
                  </div>
                </motion.div>

                {viewingTypes.map((viewingType, index) => {
                  const hasUnread = hasUnreadPoemsForType(viewingType.id);
                  return (
                    <motion.div
                      key={viewingType.id}
                      className={`vertical-vine-flower flower-${index} ${hasUnread ? 'unread-poem' : ''}`}
                      initial={{ scale: 0, rotate: -180, opacity: 0 }}
                      animate={{
                        scale: 1,
                        rotate: 0,
                        opacity: 1
                      }}
                      transition={{
                        delay: 1.5 + index * 0.25,
                        type: "spring",
                        stiffness: 180,
                        damping: 12
                      }}
                      whileHover={{
                        scale: 1.2,
                        rotate: [0, -5, 5, -5, 0],
                        transition: { duration: 0.5 }
                      }}
                      onClick={() => handleViewingTypeClick(viewingType)}
                    >
                      <div className="flower-emoji" style={{ fontSize: '80px' }}>
                        🌸
                      </div>

                      <div className="vertical-vine-flower-label">
                        {viewingType.readLabel}
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </motion.div>
        )}

        {viewMode === 'poems' && (
          <motion.div
            key="poems"
            className="poems-selection-container"
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -100 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="poems-title">
              {selectedViewingType?.name} Poems
            </h2>

            {filteredPoems.length === 0 ? (
              <div className="no-poems-message">
                <p>No poetry flowers in this garden...</p>
                <p>Visit the Journal to grow one!</p>
                <motion.button
                  className="back-to-garden-btn"
                  onClick={handleBackToViewingTypes}
                  whileHover={{ scale: 1.05, x: -5 }}
                  whileTap={{ scale: 0.95 }}
                >
                  ← Back to Garden
                </motion.button>
              </div>
            ) : (
              <div className="vine-garden-vertical" style={{ minHeight: `${Math.max(900, (filteredPoems.length + 1) * 150)}px` }}>
                {/* Detailed Vertical Vine with Thorns - Background */}
                <svg className="vertical-vine-svg" viewBox="0 0 400 1000" preserveAspectRatio="xMidYMid meet" style={{ height: `${Math.max(1000, (filteredPoems.length + 1) * 150)}px` }}>
                  <defs>
                    <linearGradient id="poemsVineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#2D5016" />
                      <stop offset="50%" stopColor="#4A7C59" />
                      <stop offset="100%" stopColor="#2D5016" />
                    </linearGradient>
                  </defs>

                  {/* Main vertical vine */}
                  <motion.path
                    d="M 200 50 Q 180 150, 200 250 T 200 450 Q 220 550, 200 650 T 200 900"
                    stroke="url(#poemsVineGradient)"
                    strokeWidth="18"
                    fill="none"
                    strokeLinecap="round"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 3, ease: "easeInOut" }}
                  />

                  {/* Vine texture lines */}
                  <motion.path
                    d="M 200 50 Q 185 150, 200 250 T 200 450 Q 215 550, 200 650 T 200 900"
                    stroke="#6B8E23"
                    strokeWidth="3"
                    fill="none"
                    opacity="0.4"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 3, delay: 0.2 }}
                  />

                  {/* Thorns along the vine */}
                  {[...Array(24)].map((_, i) => {
                    const y = 80 + i * 35;
                    const x = 200 + (i % 2 === 0 ? 9 : -9);
                    const direction = i % 2 === 0 ? 1 : -1;
                    return (
                      <motion.path
                        key={`thorn-${i}`}
                        d={`M ${x} ${y} L ${x + direction * 12} ${y - 8} L ${x + direction * 8} ${y + 2} Z`}
                        fill="#8B4513"
                        stroke="#654321"
                        strokeWidth="0.5"
                        initial={{ scale: 0, opacity: 0 }}
                        animate={{ scale: 1, opacity: 0.9 }}
                        transition={{ delay: 1 + i * 0.05, duration: 0.3 }}
                      />
                    );
                  })}

                  {/* Small leaves scattered along vine */}
                  {[...Array(16)].map((_, i) => {
                    const y = 100 + i * 50;
                    const x = 200 + ((i % 3) - 1) * 25;
                    return (
                      <motion.ellipse
                        key={`leaf-${i}`}
                        cx={x}
                        cy={y}
                        rx="12"
                        ry="18"
                        fill="#6B8E23"
                        opacity="0.6"
                        transform={`rotate(${(i % 2) * 60 - 30} ${x} ${y})`}
                        initial={{ scale: 0, opacity: 0 }}
                        animate={{ scale: 1, opacity: 0.6 }}
                        transition={{ delay: 0.8 + i * 0.08, duration: 0.4 }}
                      />
                    );
                  })}
                </svg>

                {/* Back button and Poem Flowers on the vine */}
                <div className="flowers-on-vertical-vine" style={{ height: `${Math.max(900, (filteredPoems.length + 1) * 150)}px` }}>
                  {/* Back flower - first position */}
                  <motion.div
                    className="vertical-vine-flower flower-back"
                    style={{
                      cursor: 'pointer',
                      top: '5%'
                    }}
                    initial={{ scale: 0, rotate: -180, opacity: 0 }}
                    animate={{ scale: 1, rotate: 0, opacity: 1 }}
                    transition={{ delay: 1.5, type: "spring", stiffness: 180, damping: 12 }}
                    whileHover={{ scale: 1.2, rotate: [0, -5, 5, -5, 0], transition: { duration: 0.5 } }}
                    onClick={handleBackToViewingTypes}
                  >
                    <div className="flower-emoji" style={{ fontSize: '80px' }}>
                      🌸
                    </div>
                    <div className="vertical-vine-flower-label">
                      Back
                    </div>
                  </motion.div>

                  {/* Poem flowers - alternating left-right */}
                  {filteredPoems.map((poem, index) => {
                    const flowerIndex = index;
                    const topPercent = 12 + flowerIndex * 16;
                    const isUnread = !isPoemRead(poem.id) && poem.authorEmail !== currentUser?.email;

                    return (
                      <motion.div
                        key={poem.id}
                        className={`vertical-vine-flower flower-${flowerIndex} ${isUnread ? 'unread-poem' : ''}`}
                        style={{
                          top: `${topPercent}%`
                        }}
                        initial={{ scale: 0, rotate: -180, opacity: 0 }}
                        animate={{ scale: 1, rotate: 0, opacity: 1 }}
                        transition={{
                          delay: 1.5 + flowerIndex * 0.25,
                          type: "spring",
                          stiffness: 180,
                          damping: 12
                        }}
                        whileHover={{
                          scale: 1.2,
                          rotate: [0, -5, 5, -5, 0],
                          transition: { duration: 0.5 }
                        }}
                        onClick={() => handlePoemClick(poem)}
                      >
                        <div className="flower-emoji" style={{ fontSize: '80px' }}>
                          🌸
                        </div>
                        <div className="vertical-vine-flower-label poem-title">
                          {poem.title || 'Untitled'}
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            )}
          </motion.div>
        )}

        {viewMode === 'reading' && (
          <motion.div
            key="reading"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1 }}
          >
            {renderPoemView()}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Poetry;
